import {
  ApiClient,
  AuthSession,
  RealtimeClient,
  ShortcutRegistry,
  WebAuthApi,
  WebSessionRefresher,
  csrfHeaders,
} from '@sunucupark/client-core';
import type { Client } from '@sunucupark/client-react';
import { createWebPlatform, type WebPlatform } from '@/platform';
import { config } from './config';
import { defaultShortcuts } from './shortcuts';

export interface WebClient extends Client {
  readonly auth: WebAuthApi;
  /** The live channel; one stream per browser, whichever tab holds the lock (ADR 0008). */
  readonly realtime: RealtimeClient;
  readonly platformAdapters: WebPlatform;
}

/**
 * The composition root: everything is constructed once, here, and handed to the
 * app through context. No component ever builds its own client (ADR 0006).
 */
export function createWebClient(options: {
  locale: () => string;
  isMac: boolean;
  /** Product name, for the tab title. */
  title: string;
  /** The brand's favicon, which the unread badge is drawn over. */
  iconUrl: string;
  /** Colour of the unread dot. */
  badgeColor: string;
}): WebClient {
  const platform = createWebPlatform({ title: options.title, iconUrl: options.iconUrl, badgeColor: options.badgeColor });
  const clock = { now: () => Date.now() };

  const publicApi = new ApiClient({
    baseUrl: config.gatewayUrl,
    transport: platform.transport,
    client: { version: config.version, platform: config.platform },
    locale: options.locale,
    network: platform.network,
    defaultHeaders: () => ({ ...csrfHeaders }),
  });
  const auth = new WebAuthApi(publicApi);
  const session = new AuthSession({
    store: platform.sessionStore,
    refresher: new WebSessionRefresher(auth),
    clock,
    lock: platform.lock,
  });
  const api = new ApiClient({
    baseUrl: config.gatewayUrl,
    transport: platform.transport,
    client: { version: config.version, platform: config.platform },
    locale: options.locale,
    network: platform.network,
    session,
    defaultHeaders: () => ({ ...csrfHeaders }),
  });

  const realtime = new RealtimeClient({
    baseUrl: config.gatewayUrl,
    stream: platform.eventStream,
    session,
    clock,
    scheduler: { delay: (ms, signal) => new Promise((resolve) => wait(ms, resolve, signal)) },
    lock: platform.lock,
    broadcast: platform.broadcast,
  });

  const shortcuts = new ShortcutRegistry({ clock, isMac: options.isMac });
  shortcuts.register(defaultShortcuts);

  return {
    api,
    session,
    shortcuts,
    network: platform.network,
    platform: {
      badge: platform.ports.badge,
      clipboard: platform.ports.clipboard,
      links: platform.ports.links,
      notifications: platform.ports.notifications,
    },
    auth,
    realtime,
    platformAdapters: platform,
  };
}

/** A cancellable delay; the realtime client's back-off uses it. */
function wait(ms: number, done: () => void, signal?: AbortSignal): void {
  const timer = setTimeout(done, ms);
  signal?.addEventListener(
    'abort',
    () => {
      clearTimeout(timer);
      done();
    },
    { once: true },
  );
}
